import { PaperPlaneRight, Plus, X } from "@phosphor-icons/react";
import clsx from "clsx";
import { useEffect, useRef, useState } from "react";
import TextareaAutosize from "react-textarea-autosize";

import {
  FileAttachment,
  FileAttachments,
  useAttachments,
} from "features/attachments";
import { VoiceRecording } from "features/voice-message/components";

import RenderIf from "common/components/RenderIf";
import { Button } from "common/components/ui/Button";
import ProfileImage from "common/components/ui/ProfileImage";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "common/components/ui/Tooltip";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import { useActiveConversationContext } from "../../../context/ActiveConversationContext";
import useFileUpload from "../../../datahooks/useFileUpload";
import useInsertText from "../../../hooks/useInsertText";
import { useNewConversation } from "../../../hooks/useNewConversation";
import EditMessageActions from "./EditMessageActions";
import MessageOptions from "./MessageOptions";
import RecordButton from "./RecordButton";
import SettingsPopover from "./SettingsPopover";

import type React from "react";
import type { UseFormReturn } from "react-hook-form";
import type { InboxMessageSchemaType } from "../../../schemas";
import type { ConversationMessage } from "../../../types";

function loadCtrlToSend() {
  const savedCtrlToSend = localStorage.getItem("ctrlToSend");
  if (savedCtrlToSend) {
    return JSON.parse(savedCtrlToSend);
  }
  return true;
}

interface MessageFieldProps {
  editMessage: ConversationMessage;
  cancelEdit: () => void;
  onSubmit: () => void;
  isLoading: boolean;
  formReturn: UseFormReturn<InboxMessageSchemaType>;
  hasMessageFromAnotherUser: boolean;
}

export default function MessageField({
  editMessage,
  cancelEdit,
  onSubmit,
  isLoading,
  formReturn,
  hasMessageFromAnotherUser,
}: MessageFieldProps) {
  const { register, setValue, getValues, reset, watch } = formReturn;

  const isDesktop = useTwBreakpoint("lg");
  const { accounts } = useSelectedWorkspaceContext();
  const { activeConversation } = useActiveConversationContext();
  const { newConversation } = useNewConversation();

  const [isMessageActionsOpen, setIsMessageActionsOpen] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [ctrlToSend, setCtrlToSend] = useState(loadCtrlToSend());

  const prevEditMessageRef = useRef<ConversationMessage | null>(null);
  const messageFieldRef = useRef<HTMLTextAreaElement>(null);

  const insertText = useInsertText(messageFieldRef, (string) =>
    setValue("message", string),
  );

  const accountId = activeConversation
    ? activeConversation.owner
    : newConversation.owner;

  const { uploadFile, isUploading } = useFileUpload({ accountId });

  const {
    attachments,
    onUpload,
    onUploadCancel,
    removeAttachment,
    resetAttachments,
  } = useAttachments([], async (file, abortSignal) => {
    const {
      file_identifier,
      file_input: { size, originalname, mimetype },
    } = await uploadFile({ file, filename: file.name, abortSignal });

    const fileUrl = URL.createObjectURL(file);

    const existingAttachments = getValues("attachments");
    const uploadedAttachment = {
      id: file_identifier,
      name: originalname,
      media_type: mimetype,
      url: fileUrl,
      byte_size: size,
    };
    setValue(
      "attachments",
      existingAttachments
        ? [...existingAttachments, uploadedAttachment]
        : [uploadedAttachment],
      { shouldDirty: true },
    );

    return {
      id: file_identifier,
      byteSize: size,
      name: originalname,
      mimeType: mimetype,
      url: fileUrl,
    };
  });

  useEffect(() => {
    if (editMessage !== prevEditMessageRef.current) {
      reset({ message: editMessage?.body });
      prevEditMessageRef.current = editMessage;
    }
  }, [editMessage, reset]);

  useEffect(() => {
    setIsRecording(false);
    resetAttachments();
  }, [newConversation, activeConversation]);

  function onRemoveAttachment(attachmentId: string) {
    setValue(
      "attachments",
      getValues("attachments").filter(({ id }) => id !== attachmentId),
      { shouldDirty: true },
    );
    removeAttachment(attachmentId);
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Enter") {
      if (e.ctrlKey || e.metaKey) {
        if (ctrlToSend) {
          onSubmit();
        } else {
          setValue("message", `${getValues("message")}\n`);
        }
      } else if (!ctrlToSend) {
        onSubmit();
        e.preventDefault();
      }
    }
  }

  const isEmptyMessage = !(
    watch("message")?.trim() ||
    attachments.length ||
    watch("audio")
  );

  // Disable messaging if recipient isn’t a lead, not connected, or last message was declined
  const isMessagingDisabled = activeConversation
    ? (!activeConversation.participants[0].is_lead &&
        !activeConversation.connected) ||
      activeConversation.last_message.declined
    : false;

  const account = accounts.find(({ id }) => id === accountId);

  // Messaging is blocked if disabled with no incoming message, or user is not logged in
  const isMessagingBlocked =
    (isMessagingDisabled && !hasMessageFromAnotherUser) ||
    account.state !== "LoggedIn";

  const { ref, ...registerRest } = register("message");

  const sender = accounts.find(({ id }) => id === accountId);

  const messageOptions = (
    <MessageOptions
      isMessagingDisabled={isMessagingBlocked}
      uploadedFilesCount={attachments.length}
      ctrlToSend={ctrlToSend}
      setCtrlToSend={setCtrlToSend}
      onFileUpload={onUpload}
      insertText={insertText}
      isRecording={isRecording}
      setIsRecording={setIsRecording}
    />
  );

  const sendButton = (
    <Button
      variant="default-purple"
      intent={isDesktop ? "default" : "iconOnly"}
      isLoading={isLoading}
      disabled={isEmptyMessage || isMessagingBlocked || isUploading}
      leftIcon={isDesktop && <PaperPlaneRight />}
      onClick={onSubmit}
      size={isDesktop ? "sm" : "md"}
    >
      {isDesktop ? "Send" : <PaperPlaneRight />}
    </Button>
  );

  const voiceRecordingComponent = (
    <VoiceRecording
      onRecordingEnd={(audioBlob) => {
        setValue("audio", audioBlob);
      }}
      onRecordingDelete={() => {
        setIsRecording(false);
        reset({ message: "" });
      }}
    />
  );

  return (
    <>
      <div
        className={clsx(
          isMessagingBlocked ? "text-black-500" : "text-black-950",
          "flex w-full items-center gap-2 rounded-t-3xl border border-b-0 border-white text-sm transition-all lg:flex-col lg:items-stretch lg:gap-0 lg:rounded-3xl lg:border-b lg:border-black-200 lg:bg-white",
          "has-[textarea:hover:focus-within]:bg-white",
          "p-2 has-[textarea:focus-visible]:border-purple-400",
          !isDesktop && isRecording && "justify-between border-purple-400",
        )}
      >
        <RenderIf condition={!isDesktop && !isRecording}>
          <Button
            variant={isMessageActionsOpen ? "secondary-black" : "ghost-black"}
            intent="iconOnly"
            onClick={() => setIsMessageActionsOpen(!isMessageActionsOpen)}
          >
            {isMessageActionsOpen ? <X /> : <Plus />}
          </Button>
        </RenderIf>

        {!isDesktop && isRecording ? (
          voiceRecordingComponent
        ) : (
          <div className="flex w-full flex-col gap-2 overflow-x-hidden rounded-5xl bg-black-100 p-2 lg:gap-0 lg:rounded-none lg:bg-transparent lg:p-0">
            <div className="flex flex-row gap-2">
              <Tooltip>
                <TooltipTrigger asChild>
                  <ProfileImage
                    size="xs"
                    className={clsx([
                      isMessagingBlocked && "opacity-50",
                      "mt-0.5 rounded-full",
                    ])}
                    src={sender.picture_url}
                    alt={sender.full_name}
                  />
                </TooltipTrigger>

                <TooltipContent className="w-fit">
                  {sender.full_name}
                </TooltipContent>
              </Tooltip>

              {isRecording && isDesktop ? (
                voiceRecordingComponent
              ) : (
                <TextareaAutosize
                  id="inbox-text-area"
                  minRows={isDesktop ? 2 : 1}
                  maxRows={11.3}
                  disabled={isMessagingBlocked}
                  placeholder="Write your message here"
                  onKeyDown={onKeyDown}
                  className="flex-1 resize-none bg-transparent py-1 outline-none scrollbar-thin"
                  ref={(el) => {
                    ref(el);
                    messageFieldRef.current = el;
                  }}
                  {...registerRest}
                />
              )}
            </div>

            <RenderIf condition={!!attachments.length}>
              <FileAttachments
                attachments={attachments}
                renderAttachment={(attachment) => {
                  const props =
                    "file" in attachment
                      ? {
                          file: attachment.file,
                          errorMessage:
                            "errorMessage" in attachment &&
                            attachment.errorMessage,
                          onRemove: () => {
                            onUploadCancel(attachment.file);
                          },
                        }
                      : {
                          fileData: {
                            name: attachment.name,
                            byteSize: attachment.byteSize,
                            mimeType: attachment.mimeType,
                            url: attachment.url,
                          },
                          onRemove: () => {
                            onRemoveAttachment(attachment.id);
                          },
                        };

                  return <FileAttachment {...props} />;
                }}
              />
            </RenderIf>
          </div>
        )}

        {(isEmptyMessage || isMessagingBlocked) &&
        !isDesktop &&
        !isRecording ? (
          <RecordButton
            isRecording={isRecording}
            setIsRecording={setIsRecording}
            isDisabled={isMessagingDisabled}
          />
        ) : (
          <RenderIf condition={!isDesktop}>{sendButton}</RenderIf>
        )}

        {/* Note: Add padding right on the desktop because of the Intercom widget overlap */}
        <div className="mt-2 hidden flex-row items-center justify-between border-t border-black-200 pt-2 lg:flex">
          {messageOptions}

          {editMessage ? (
            <EditMessageActions
              isUpdatingMessage={isLoading}
              onCancel={cancelEdit}
              onConfirm={onSubmit}
            />
          ) : (
            <div className="flex flex-row items-center justify-between gap-2">
              {/* Settings Popover */}
              <SettingsPopover
                ctrlToSend={ctrlToSend}
                setCtrlToSend={setCtrlToSend}
                isDisabled={isMessagingBlocked}
              />

              <RenderIf condition={isDesktop}>{sendButton}</RenderIf>
            </div>
          )}
        </div>
      </div>

      <RenderIf condition={!isDesktop && isMessageActionsOpen && !isRecording}>
        <div className="border-t border-black-200 p-2">{messageOptions}</div>
      </RenderIf>
    </>
  );
}
